import { InMemoryProvider, OpenFeature } from '@openfeature/web-sdk';
import { createContext, ReactNode, useContext, useState } from 'react';
import { ConfigClient } from '../../config';

const FeatureFlagContext = createContext({
    getBooleanValue: (_flagName: string, defaultValue: boolean) => defaultValue,
});

function buildFlagConfiguration(
    flags: Record<string, boolean>
): Record<string, { variants: Record<string, boolean>; defaultVariant: string; disabled: boolean }> {
    const config: Record<string, { variants: Record<string, boolean>; defaultVariant: string; disabled: boolean }> = {};

    for (const [key, value] of Object.entries(flags)) {
        config[key] = {
            variants: { on: true, off: false },
            defaultVariant: value ? 'on' : 'off',
            disabled: false,
        };
    }

    return config;
}

export const FeatureFlagProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [isReady] = useState(() => {
        const flags = ConfigClient.featureFlags(window);
        const flagConfig = buildFlagConfiguration(flags);
        const provider = new InMemoryProvider(flagConfig);
        OpenFeature.setProvider(provider);
        return true;
    });

    const getBooleanValue = (flagName: string, defaultValue: boolean): boolean => {
        if (!isReady) return defaultValue;
        const client = OpenFeature.getClient();
        return client.getBooleanValue(flagName, defaultValue);
    };

    return <FeatureFlagContext.Provider value={{ getBooleanValue }}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlag = (flagName: string): boolean => {
    const { getBooleanValue } = useContext(FeatureFlagContext);
    return getBooleanValue(flagName, false);
};
